import { convexQuery } from '@convex-dev/react-query'
import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { FunctionReturnType } from 'convex/server'
import { IconChart2Outline18, IconXmarkOutline18 } from 'nucleo-ui-outline-18'
import { useState } from 'react'
import { Button } from '~/components/ui/button'
import { EmptyState } from '~/components/ui/empty-state'
import { PageHeader } from '~/components/ui/page-header'
import { SegmentedControl, SegmentedControlItem } from '~/components/ui/segmented-control'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { Spinner } from '~/components/ui/spinner'
import { formatRelativeTime } from '~/lib/datetime'
import { titleFor } from '~/lib/route-title'
import { cn } from '~/lib/utils'
import { api } from '~convex/_generated/api'
import type { Id } from '~convex/_generated/dataModel'
import type { AiCallPurpose } from '~convex/lib/aiCallEnums'
import { formatMicroUsd } from '~convex/lib/aiCost'

export const Route = createFileRoute('/admin/ai')({
  component: AdminAiCalls,
  head: () => ({ meta: [{ title: titleFor(msg`AI calls`) }] }),
})

type Window = 'today' | '7d' | '30d' | '90d'
type CallRow = FunctionReturnType<typeof api.adminAiCalls.list>['rows'][number]

const WINDOWS: { label: MessageDescriptor; value: Window }[] = [
  { label: msg`Today`, value: 'today' },
  { label: msg`7 days`, value: '7d' },
  { label: msg`30 days`, value: '30d' },
  { label: msg`90 days`, value: '90d' },
]

/** Base UI selects cannot hold `undefined`, so "no filter" needs a value. */
const ALL = '__all__'

const PURPOSE_LABEL: Record<AiCallPurpose, MessageDescriptor> = {
  assistant_briefing: msg`Home briefing`,
  assistant_classify: msg`Turn classifier`,
  assistant_embedding: msg`Search embedding`,
  assistant_summary: msg`Conversation summary`,
  assistant_title: msg`Thread title`,
  assistant_transcription: msg`Voice transcription`,
  assistant_turn: msg`Assistant turn`,
  catalog_embedding: msg`Catalog embedding`,
  render_image: msg`Render image`,
  render_objects: msg`Render object map`,
  render_translate: msg`Ficha translation`,
  render_viewport: msg`Viewport crop`,
  visit_notes: msg`Visit report`,
  visit_suggestions: msg`Visit suggestions`,
  visit_transcription: msg`Visit transcription`,
}

const PURPOSES = Object.keys(PURPOSE_LABEL).sort()

const isPurpose = (value: string): value is AiCallPurpose => value in PURPOSE_LABEL

const Stat = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <div className="flex flex-col gap-0.5 rounded-lg border bg-card px-4 py-3">
    <span className="text-muted-foreground text-xs">{label}</span>
    <span className="font-heading font-semibold text-xl tabular-nums">{value}</span>
    {hint === undefined ? null : <span className="text-[11px] text-muted-foreground">{hint}</span>}
  </div>
)

/** One rollup table: the spend cut by purpose, model or workspace. */
const Breakdown = ({
  rows,
  title,
  total,
}: {
  rows: { calls: number; costMicroUsd: number; errors: number; key: string; name?: string | null }[]
  title: string
  total: number
}) => {
  const { i18n } = useLingui()
  return (
    <section className="flex min-w-0 flex-1 flex-col gap-2">
      <h2 className="font-medium text-muted-foreground text-xs">{title}</h2>
      <div className="overflow-hidden rounded-lg border">
        {rows.length === 0 ? (
          <p className="px-3 py-4 text-center text-muted-foreground text-xs">
            <Trans>No calls in this window.</Trans>
          </p>
        ) : (
          rows.map((row) => (
            <div className="flex flex-col gap-1 border-b px-3 py-2 last:border-b-0" key={row.key}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate text-sm">{row.name ?? row.key}</span>
                <span className="shrink-0 font-medium text-sm tabular-nums">
                  {formatMicroUsd(row.costMicroUsd)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
                  <span
                    className="block h-1 bg-primary/70"
                    style={{
                      width: `${total === 0 ? 0 : Math.max(2, (row.costMicroUsd / total) * 100)}%`,
                    }}
                  />
                </span>
                <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums">
                  {i18n.number(row.calls)}
                  {row.errors > 0 ? ` · ${i18n.number(row.errors)} err` : ''}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  )
}

type CallDetailData = NonNullable<FunctionReturnType<typeof api.adminAiCalls.call>>

/** The payload drawer: what was actually sent, and what came back. */
const CallDetail = ({ callId, onClose }: { callId: Id<'aiCalls'>; onClose: () => void }) => {
  const { t, i18n } = useLingui()
  const { data } = useQuery(convexQuery(api.adminAiCalls.call, { callId }))
  const loaded = data ?? null
  return (
    <aside className="fixed inset-y-0 right-0 z-50 flex w-[min(760px,92vw)] flex-col border-l bg-background shadow-xl">
      <header className="flex items-center justify-between gap-2 border-b px-4 py-3">
        <div className="min-w-0">
          <h2 className="truncate font-heading font-medium text-sm">
            {loaded === null
              ? t`Call`
              : `${i18n._(PURPOSE_LABEL[loaded.purpose])} · ${loaded.model}`}
          </h2>
          {loaded === null ? null : (
            <p className="truncate text-muted-foreground text-xs">
              {loaded.workspaceName ?? t`Unknown workspace`} ·{' '}
              {i18n.date(new Date(loaded.createdAt), { dateStyle: 'short', timeStyle: 'medium' })}
            </p>
          )}
        </div>
        <Button aria-label={t`Close`} onClick={onClose} size="icon-sm" variant="ghost">
          <IconXmarkOutline18 className="size-4" />
        </Button>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {data === undefined ? (
          <Spinner className="size-5" />
        ) : data === null ? (
          <EmptyState icon={IconChart2Outline18} title={t`Call not found`} />
        ) : (
          <CallDetailBody data={data} />
        )}
      </div>
    </aside>
  )
}

const CallDetailBody = ({ data }: { data: CallDetailData }) => {
  const { t, i18n } = useLingui()
  return (
    <div className="flex flex-col gap-4">
      <dl className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
        <div>
          <dt className="text-muted-foreground">
            <Trans>Cost</Trans>
          </dt>
          <dd className="tabular-nums">
            {data.costMicroUsd === undefined ? t`unpriced` : formatMicroUsd(data.costMicroUsd)}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">
            <Trans>Duration</Trans>
          </dt>
          <dd className="tabular-nums">{(data.durationMs / 1000).toFixed(1)}s</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">
            <Trans>Tokens in</Trans>
          </dt>
          <dd className="tabular-nums">
            {i18n.number(
              (data.usage.inputTokens ?? 0) +
                (data.usage.cacheReadTokens ?? 0) +
                (data.usage.cacheWriteTokens ?? 0)
            )}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">
            <Trans>Tokens out</Trans>
          </dt>
          <dd className="tabular-nums">{i18n.number(data.usage.outputTokens ?? 0)}</dd>
        </div>
      </dl>
      {data.errorMessage === undefined ? null : (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-destructive text-xs">
          {data.errorMessage}
        </p>
      )}
      {data.attachments.length === 0 ? null : (
        <div className="text-xs">
          <span className="text-muted-foreground">
            <Trans>Images</Trans>
          </span>
          <ul className="mt-1 flex flex-col gap-0.5 font-mono text-[11px]">
            {data.attachments.map((key) => (
              <li className="truncate" key={key}>
                {key}
              </li>
            ))}
          </ul>
        </div>
      )}
      {data.truncated ? (
        <p className="text-[11px] text-muted-foreground">
          <Trans>This call's payloads were too large to store whole. The middle is omitted.</Trans>
        </p>
      ) : null}
      <Payload label={t`Request`} text={data.request} />
      <Payload label={t`Response`} text={data.response} />
    </div>
  )
}

const Payload = ({ label, text }: { label: string; text: string }) => {
  const body = text === '' ? '—' : text
  return (
    <section className="flex min-w-0 flex-col gap-1">
      <h3 className="font-medium text-muted-foreground text-xs">{label}</h3>
      <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted/50 p-3 font-mono text-[11px] leading-relaxed">
        {body}
      </pre>
    </section>
  )
}

const CallList = ({
  onSelect,
  rows,
}: {
  onSelect: (id: Id<'aiCalls'>) => void
  rows: CallRow[]
}) => {
  const { i18n } = useLingui()
  return (
    <div className="overflow-hidden rounded-lg border">
      {rows.map((row) => (
        <button
          className="flex w-full items-center gap-3 border-b px-3 py-2 text-left last:border-b-0 hover:bg-accent"
          key={row._id}
          onClick={() => onSelect(row._id)}
          type="button"
        >
          <span
            className={cn(
              'size-1.5 shrink-0 rounded-full',
              row.status === 'error' ? 'bg-destructive' : 'bg-green-600'
            )}
          />
          <span className="w-40 shrink-0 truncate text-sm">
            {i18n._(PURPOSE_LABEL[row.purpose])}
          </span>
          <span className="w-44 shrink-0 truncate text-muted-foreground text-xs">{row.model}</span>
          <span className="min-w-0 flex-1 truncate text-muted-foreground text-xs">
            {row.workspaceName ?? '—'}
          </span>
          <span className="w-16 shrink-0 text-right text-xs tabular-nums">
            {row.costMicroUsd === undefined ? '—' : formatMicroUsd(row.costMicroUsd)}
          </span>
          <span className="w-14 shrink-0 text-right text-muted-foreground text-xs tabular-nums">
            {(row.durationMs / 1000).toFixed(1)}s
          </span>
          <span className="w-20 shrink-0 text-right text-muted-foreground text-xs">
            {formatRelativeTime(row.createdAt, i18n)}
          </span>
        </button>
      ))}
    </div>
  )
}

function AdminAiCalls() {
  const { t, i18n } = useLingui()
  // Not `window` — that name shadows the global for the whole component body,
  // so any later `window.matchMedia`/`location` would resolve to this string.
  const [timeWindow, setTimeWindow] = useState<Window>('7d')
  const [purpose, setPurpose] = useState<string>(ALL)
  const [onlyErrors, setOnlyErrors] = useState(false)
  const [selected, setSelected] = useState<Id<'aiCalls'> | null>(null)

  const { data: usage } = useQuery(convexQuery(api.adminAiCalls.usage, { window: timeWindow }))
  const { data: calls } = useQuery(
    convexQuery(api.adminAiCalls.list, {
      onlyErrors: onlyErrors ? true : undefined,
      purpose: isPurpose(purpose) ? purpose : undefined,
      window: timeWindow,
    })
  )

  const purposeItems: Record<string, string> = { [ALL]: t`All purposes` }
  for (const key of PURPOSES) {
    if (isPurpose(key)) {
      purposeItems[key] = i18n._(PURPOSE_LABEL[key])
    }
  }

  return (
    <div className="flex flex-col gap-5 p-6">
      <PageHeader title={t`AI calls`} />
      <p className="-mt-3 text-muted-foreground text-sm">
        <Trans>
          Every model call the product makes: what it cost, how long it took, and exactly what was
          sent.
        </Trans>
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <SegmentedControl
          onValueChange={(value: Window) => setTimeWindow(value)}
          value={timeWindow}
        >
          {WINDOWS.map((entry) => (
            <SegmentedControlItem key={entry.value} value={entry.value}>
              {i18n._(entry.label)}
            </SegmentedControlItem>
          ))}
        </SegmentedControl>
        <Select
          items={purposeItems}
          onValueChange={(value) => setPurpose(value ?? ALL)}
          value={purpose}
        >
          <SelectTrigger aria-label={t`Purpose`} className="w-52">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t`All purposes`}</SelectItem>
            {PURPOSES.map((key) =>
              isPurpose(key) ? (
                <SelectItem key={key} value={key}>
                  {i18n._(PURPOSE_LABEL[key])}
                </SelectItem>
              ) : null
            )}
          </SelectContent>
        </Select>
        <Button
          onClick={() => setOnlyErrors((value) => !value)}
          size="sm"
          variant={onlyErrors ? 'default' : 'outline'}
        >
          <Trans>Errors only</Trans>
        </Button>
      </div>

      {usage === undefined ? (
        <Spinner className="size-5" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat
              hint={t`prices verified ${usage.pricesVerifiedOn}`}
              label={t`Spend`}
              value={formatMicroUsd(usage.costMicroUsd)}
            />
            <Stat
              hint={usage.partial ? t`most recent 5,000` : undefined}
              label={t`Calls`}
              value={i18n.number(usage.calls)}
            />
            <Stat
              hint={usage.unpricedCalls > 0 ? t`${usage.unpricedCalls} unpriced` : undefined}
              label={t`Errors`}
              value={i18n.number(usage.errors)}
            />
            <Stat label={t`Model time`} value={`${Math.round(usage.totalDurationMs / 1000)}s`} />
          </div>

          <div className="flex flex-col gap-4 lg:flex-row">
            <Breakdown
              rows={usage.byPurpose.map((row) => ({
                ...row,
                name: isPurpose(row.key) ? i18n._(PURPOSE_LABEL[row.key]) : row.key,
              }))}
              title={t`By purpose`}
              total={usage.costMicroUsd}
            />
            <Breakdown rows={usage.byModel} title={t`By model`} total={usage.costMicroUsd} />
            <Breakdown
              rows={usage.byWorkspace}
              title={t`By workspace`}
              total={usage.costMicroUsd}
            />
          </div>
        </>
      )}

      <section className="flex flex-col gap-2">
        <h2 className="font-medium text-muted-foreground text-xs">
          <Trans>Calls</Trans>
        </h2>
        {calls === undefined ? (
          <Spinner className="size-5" />
        ) : calls.rows.length === 0 ? (
          <EmptyState
            description={t`Nothing matched these filters in this window.`}
            icon={IconChart2Outline18}
            title={t`No calls`}
          />
        ) : (
          <>
            <CallList onSelect={setSelected} rows={calls.rows} />
            {calls.truncated ? (
              <p className="text-[11px] text-muted-foreground">
                <Trans>
                  Showing the newest {calls.rows.length} of {calls.matched} matching calls.
                </Trans>
              </p>
            ) : null}
          </>
        )}
      </section>

      {selected === null ? null : (
        <CallDetail callId={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  )
}
